import React from 'react';
import Grid from '@mui/material/GridLegacy';
import { Box, Typography, Button, Card, CardContent, CardActions, Chip } from '@mui/material';
import { Add, Edit, Delete, Category } from '@mui/icons-material';
import CategoryIconByName from '../../../components/common/CategoryIconByName';

export default function CategoriesTab({
  categories,
  onAddCategory,
  onEditCategory,
  onDeleteCategory,
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Categories Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onAddCategory}
          sx={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            boxShadow: '0 4px 15px rgba(79, 172, 254, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
              boxShadow: '0 6px 20px rgba(79, 172, 254, 0.5)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Add New Category
        </Button>
      </Box>

      {!categories?.length ? (
        <Card
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <Category sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No categories yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Add New Category" to get started
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {categories.map((category) => (
            <Grid item xs={12} sm={6} md={4} key={category._id}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        mr: 2,
                        bgcolor: category.color,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      {category.icon ? (
                        <CategoryIconByName iconName={category.icon} size={32} color="white" />
                      ) : (
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
                          {category.name?.charAt(0)?.toUpperCase()}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {category.name}
                      </Typography>
                      <Chip
                        label={category.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: category.isActive ? 'rgba(46, 125, 50, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                          color: category.isActive ? 'success.main' : 'text.secondary',
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  </Box>
                  {category.description && (
                    <Typography color="text.secondary" sx={{ mb: 2, fontSize: '0.9rem' }}>
                      {category.description}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.1) 0%, rgba(0, 242, 254, 0.1) 100%)',
                      border: '1px solid rgba(79, 172, 254, 0.2)',
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                      {category.placeCount || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      places
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => onEditCategory(category)}
                    sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => onDeleteCategory(category)}
                    sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
